import { Input } from '@frontend/shadcn/components/ui/input';
import { PlusIcon, MinusIcon, CircleInfoIcon, InfoIcon } from '@revfluence/fresh-icons/solid/esm';
import React from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { ImagePreview, VideoPreview, RichTextEditor } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Switch } from '@revfluence/fresh';
import { isVideoUrl } from '@/app/refresh-components/utils/mediaUtils';
import { useRecruitment, useAmplifyHomePage } from '../RecruitmentContext';

export const AmplifyAboutSection = () => {
  const {
    addAmplifyAboutCarouselImages,
    removeAmplifyAboutCarouselImages,
    setAmplifyAboutTitle,
    setAmplifyAboutDescription,
    setAmplifyAboutHasDescription,
    setAmplifyAboutShowSection,
  } = useRecruitment();

  const homePage = useAmplifyHomePage();

  if (!homePage) {
    return null;
  }

  const {
    about,
  } = homePage;

  const {
    carouselImages: aboutCarouselImages,
    title: aboutTitle,
    description: aboutDescription,
    hasDescription: aboutHasDescription,
    showSection: aboutShowSection,
  } = about;

  const handleAddAmplifyAboutCarouselImages = (content: string) => {
    addAmplifyAboutCarouselImages(content);
  };

  const handleRemoveAmplifyAboutCarouselImages = (index: number) => {
    removeAmplifyAboutCarouselImages(index);
  };

  const handleMediaSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          handleAddAmplifyAboutCarouselImages(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  return (
    <AccordionItem value="about" className="border-b-0">
      <AccordionTrigger>
        <div className="flex items-center gap-2 w-full">
          <div className="w-6 h-6 bg-magenta-5 rounded-lg text-primary-foreground flex items-center justify-center">
            <InfoIcon fontSize={12} />
          </div>
          <span className="text-base font-medium">About</span>
          <div className="ml-auto mr-2 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={aboutShowSection}
              onChange={(checked) => setAmplifyAboutShowSection(checked)}
              size="small"
            />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {/* Title Section */}
        <div className="flex flex-col gap-2">
          <label htmlFor="amplifyAboutTitle" className="text-xs font-medium text-foreground">Title <span className="text-primaryMuted font-normal">(Required)</span></label>
          <div className="px-[1px]">
            <Input id="amplifyAboutTitle" placeholder="Join Our Affiliate Program!" value={aboutTitle} onChange={(e) => setAmplifyAboutTitle(e.target.value)} />
          </div>
        </div>

        {/* Description Section */}
        <div className="mt-2">
          {aboutHasDescription && (
          <div className="flex flex-col items-start gap-2">
            <Button
              variant="link"
              className="gap-2 pl-0"
              onClick={() => setAmplifyAboutHasDescription(false)}
              size="xs"
            >
              <MinusIcon />
              Remove Description
            </Button>
            <RichTextEditor
              placeholder="Welcome to your page! Write something amazing about your brand and give creators more information about this collaboration."
              value={aboutDescription}
              onChange={setAmplifyAboutDescription}
              className="[&_.ql-editor]:min-h-[100px]"
            />
          </div>
            )}
          {!aboutHasDescription && (
          <Button
            variant="link"
            className="gap-2 pl-0"
            onClick={() => setAmplifyAboutHasDescription(true)}
            size="xs"
          >
            <PlusIcon />
            Add Description
          </Button>
            )}
        </div>

        {/* Content Carousel Section */}
        <div className="flex flex-col gap-2 items-start mt-4">
          <div className="flex items-center gap-1">
            <label className="text-xs font-medium">Content <span className="text-primaryMuted font-normal">(Required)</span></label>
            <CircleInfoIcon fontSize={12} className="text-grey-4" />
          </div>
          <div className="flex gap-3 flex-wrap">
            {aboutCarouselImages.map((content, index) =>
              (isVideoUrl(content) ? (
                <VideoPreview
                  key={index}
                  videoUrl={content}
                  onRemove={() => handleRemoveAmplifyAboutCarouselImages(index)}
                />
              ) : (
                <ImagePreview
                  key={index}
                  imageUrl={content}
                  onRemove={() => handleRemoveAmplifyAboutCarouselImages(index)}
                />
              ))
            )}
          </div>
          <Button
            variant="link"
            size="xs"
            className="gap-2 pl-0"
            onClick={handleMediaSelect}
          >
            <PlusIcon />
            Add Image or Video
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
